import { Component, Input, OnInit, SkipSelf, Output, EventEmitter, Optional, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { LookUpServiceAndValidation } from '../../../services/look-up-and-validation.service';
import { MinsterShowHideComponent } from "../minster-show-hide/minster-show-hide.component";
import { MinsterSelectComponent } from "../minster-select/minster-select.component";
import { MinsterInputComponent } from "../minster-input/minster-input.component";
import { PostcodeLookUp } from '../../../models/postcode-look-up.model';
import { ControlContainer, FormsModule } from '@angular/forms';
import { StateService } from '../../../services/state.service';
import { MatButtonModule } from '@angular/material/button';
import { InputType } from '../../../models/enums.models';
import { LookUp } from '../../../models/look-up.model';
import { MatIconModule } from '@angular/material/icon';
import { Address } from '../../../models/claim.model';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'minster-address',
    standalone: true,
    imports: [MinsterShowHideComponent, MinsterSelectComponent, MinsterInputComponent, FormsModule, MatIconModule, MatButtonModule],
    templateUrl: './minster-address.component.html',
    styleUrls: ['./minster-address.component.css'],
    viewProviders: [
        {
            provide: ControlContainer,
            useFactory: (parent: ControlContainer) => parent,
            deps: [[new SkipSelf(), new Optional(), ControlContainer]]
        }
    ]
})

export class MinsterAddressComponent implements OnInit {
    @Input('address') _address?: Address;
    @Input("suffix") _suffix?: string;
    @Input('disabled') _disabled = false;
    @Output() addressChange = new EventEmitter<Address>();

    _showLookUpSelect = false;
    _looUpAddresses: LookUp[] = [];
    _lookUpAddressResults?: PostcodeLookUp;
    _selectedAddress: number = 0;
    _selectedCountry: number = 0;
    _inputTypes = InputType;
    _countries: LookUp[];

    private countryMapping: Record<string, number> = {};
    private reverseCountryMapping: Record<number, string> = {};

    constructor(
        private lookUpService: LookUpServiceAndValidation,
        private change: ChangeDetectorRef,
        private toastr: ToastrService,
        private stateService: StateService
    ) {
        this._countries = stateService.lookUps?.lookUpDictionary['AccidentCountryDigitalOnBoarding']!;
    }

    private _propagateChange = (address: Address | undefined): void => {
        this.addressChange.emit(address);
    };

    private onTouched: () => void = () => { };
    
    private onChange: (address: Address | undefined) => void = () => { };

    writeValue(address: Address | undefined): void {
        this._address = address || this.getDefaultAddress();
        this._selectedCountry = this.mapCountryToId(this._address?.country || '');
        this.change.markForCheck();
    }

    registerOnChange(fn: (address: Address | undefined) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled;
        this.change.markForCheck();
    }

    ngOnInit(): void {
        this.populateCountryMappings();

        if (!this._address) {
            this._address = this.getDefaultAddress();
        }

        if (this._address?.country) {
            this._selectedCountry = this.mapCountryToId(this._address.country);
        }

        this._propagateChange(this._address || undefined);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['_address'] && changes['_address'].currentValue) {
            if (this._address?.country) {
                this._selectedCountry = this.mapCountryToId(this._address.country);
            }

            this._propagateChange(this._address || undefined);
        }
    }

    onLookUpPostcode(postcode: string): void {
        this.lookUpService.getAddresses(postcode).subscribe({
            next: (result) => {
                this._lookUpAddressResults = result;
                this._looUpAddresses = result.addresses.map((address, index) => ({
                    description: address.full,
                    value: index + 1
                }));

                this._showLookUpSelect = true;
                this.change.markForCheck();
            },
            error: (e) => this.toastr.error(e.error)
        });
    }

    onAddressSelected(index: number): void {
        const address = this._lookUpAddressResults?.addresses[index - 1];

        if (address) {
            this._selectedCountry = this.mapCountryToId(address.country || '');
            this._address = {
                ...this._address,
                addressId: this._address?.addressId || 0,
                typeId: this._address?.typeId,
                companyName: this._address?.companyName,
                address1: address.line1 || '',
                address2: address.line2 || '',
                address3: '',
                county: address.county || '',
                country: address.country,
                city: address.townOrCity || this._address?.city || '',
                postcode: this._address?.postcode || ''
            };

            this.updateState();
            this._propagateChange(this._address!);
            this.change.markForCheck();
        }
    }

    onCountryChange(): void {
        if (this._address) {
            this._address.country = this.mapIdToCountry(this._selectedCountry);

            this.updateState();
            this._propagateChange(this._address);
        }
    }

    private populateCountryMappings(): void {
        this.countryMapping = {};
        this.reverseCountryMapping = {};
        this._countries.forEach((country) => {
            if (country.description && country.value !== undefined) {
                this.countryMapping[country.description] = country.value;
                this.reverseCountryMapping[country.value] = country.description;
            }
        });
    }

    private mapCountryToId(country: string): number {
        return this.countryMapping[country] ?? -1;
    }

    private mapIdToCountry(countryId: number): string {
        return this.reverseCountryMapping[countryId] || 'Other';
    }

    private getDefaultAddress(): Address | undefined {
        if (this._suffix === 'personal') {
            const customerAddress = this.stateService.customer?.address;

            if (customerAddress) {
                return {
                    addressId: customerAddress.addressId ?? 0, // Default to 0 if undefined
                    typeId: customerAddress.typeId ?? null,
                    companyName: customerAddress.companyName ?? '',
                    address1: customerAddress.address1 ?? '',
                    address2: customerAddress.address2 ?? '',
                    address3: customerAddress.address3 ?? '',
                    city: customerAddress.city ?? '',
                    county: customerAddress.county ?? '',
                    country: customerAddress.country ?? '',
                    postcode: customerAddress.postcode ?? ''
                };
            }
        }
        else if (this._suffix === 'third-party-owner') {
            const thirdPartyAddress = this.stateService.claim?.thirdPartyDetails.find(x => x.isPrimary)?.thirdPartyOwnerAddress;

            if (thirdPartyAddress) {
                return {
                    addressId: thirdPartyAddress.addressId ?? 0,
                    typeId: thirdPartyAddress.typeId ?? null,
                    companyName: thirdPartyAddress.companyName ?? '',
                    address1: thirdPartyAddress.address1 ?? '',
                    address2: thirdPartyAddress.address2 ?? '',
                    address3: thirdPartyAddress.address3 ?? '',
                    city: thirdPartyAddress.city ?? '',
                    county: thirdPartyAddress.county ?? '',
                    country: thirdPartyAddress.country ?? '',
                    postcode: thirdPartyAddress.postcode ?? ''
                };
            }
        }
        return undefined;
    }

    private updateState(): void {
        if (this._suffix === 'personal') {
            this.stateService.customer!.address = {
                addressId: this._address?.addressId ?? 0,
                typeId: this._address?.typeId ?? null,
                companyName: this._address?.companyName ?? '',
                address1: this._address?.address1 ?? '',
                address2: this._address?.address2 ?? '',
                address3: this._address?.address3 ?? '',
                city: this._address?.city ?? '',
                county: this._address?.county ?? '',
                country: this._address?.country ?? '',
                postcode: this._address?.postcode ?? ''
            };
        }
        else if (this._suffix === 'third-party-owner') {
            const primaryThirdParty = this.stateService.claim?.thirdPartyDetails.find(x => x.isPrimary);

            if (primaryThirdParty) {
                primaryThirdParty.thirdPartyOwnerAddress = {
                    addressId: this._address?.addressId ?? 0,
                    typeId: this._address?.typeId ?? null,
                    companyName: this._address?.companyName ?? '',
                    address1: this._address?.address1 ?? '',
                    address2: this._address?.address2 ?? '',
                    address3: this._address?.address3 ?? '',
                    city: this._address?.city ?? '',
                    county: this._address?.county ?? '',
                    country: this._address?.country ?? '',
                    postcode: this._address?.postcode ?? ''
                };
            }
        }
    }

    onFieldChange(): void {
        if (this._address) {
            this.addressChange.emit(this._address);
            this.updateState();
        }
    }
}